import {
  getPageAuthSession,
  isStoredSessionCurrent,
} from './auth';

export const CATALOG_PRODUCT_CACHE_KEY = 'productCatalog';
export const CATALOG_SELLABLE_ITEMS_CACHE_KEY = 'sellableItemsCache';
export const CATALOG_STOCKS_CACHE_KEY = 'productStocks';
export const CATALOG_PENDING_OVERRIDES_KEY = 'pendingCatalogOverrides';
export const CATALOG_ADMIN_SUMMARY_KEY = 'catalogAdminSummary';
export const CATALOG_ADMIN_GROUPS_KEY = 'catalogAdminGroups';
export const CATALOG_WAITER_PRODUCTS_KEY = 'waiterOperationalCatalog';

const CATALOG_STORAGE_KEYS = [
  CATALOG_PRODUCT_CACHE_KEY,
  CATALOG_SELLABLE_ITEMS_CACHE_KEY,
  CATALOG_STOCKS_CACHE_KEY,
  CATALOG_PENDING_OVERRIDES_KEY,
  CATALOG_ADMIN_SUMMARY_KEY,
  CATALOG_ADMIN_GROUPS_KEY,
  CATALOG_WAITER_PRODUCTS_KEY,
];
const CATALOG_SESSION_PREFIX = 'brutos-catalog-session-v1:';

const normalizePart = (value) => encodeURIComponent(String(value || '').trim());

export const getCatalogStorageSession = () => getPageAuthSession();

export const isCatalogStorageSessionCurrent = (session = getCatalogStorageSession()) => {
  const pageSession = getPageAuthSession();
  const companyId = String(session?.user?.companyId || '').trim();
  const browserSessionId = String(session?.browserSessionId || '').trim();
  const token = String(session?.token || '').trim();
  return Boolean(
    companyId
    && browserSessionId
    && token
    && pageSession?.token === token
    && pageSession?.browserSessionId === browserSessionId
    && String(pageSession?.user?.companyId || '').trim() === companyId
    && isStoredSessionCurrent(session)
  );
};

export const getCatalogSessionStorageKey = (baseKey, session = getCatalogStorageSession()) => {
  if (!CATALOG_STORAGE_KEYS.includes(baseKey) || !isCatalogStorageSessionCurrent(session)) return '';
  const companyId = normalizePart(session.user.companyId);
  const userId = normalizePart(session.user.id || session.user.email || 'authenticated-user');
  const browserSessionId = normalizePart(session.browserSessionId);
  return `${CATALOG_SESSION_PREFIX}${companyId}:${userId}:${browserSessionId}:${baseKey}`;
};

export const purgeCatalogSessionStorage = ({ keepSession = null } = {}) => {
  if (typeof window === 'undefined') return;
  const keepKeys = new Set(CATALOG_STORAGE_KEYS.map((baseKey) => (
    keepSession ? getCatalogSessionStorageKey(baseKey, keepSession) : ''
  )).filter(Boolean));
  try {
    const remove = [];
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (
        CATALOG_STORAGE_KEYS.includes(key)
        || (String(key || '').startsWith(CATALOG_SESSION_PREFIX) && !keepKeys.has(key))
      ) {
        remove.push(key);
      }
    }
    remove.forEach((key) => window.localStorage.removeItem(key));
  } catch {
    // Cache is optional; storage failures never alter the backend state.
  }
};

const prepareCatalogSessionStorage = (session) => {
  if (!isCatalogStorageSessionCurrent(session)) return false;
  purgeCatalogSessionStorage({ keepSession: session });
  return true;
};

export const readCatalogSessionJson = (baseKey, fallback, session = getCatalogStorageSession()) => {
  const key = getCatalogSessionStorageKey(baseKey, session);
  if (!key || !prepareCatalogSessionStorage(session)) return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    return raw === null ? fallback : JSON.parse(raw) ?? fallback;
  } catch {
    return fallback;
  }
};

export const writeCatalogSessionJson = (baseKey, value, session = getCatalogStorageSession()) => {
  const key = getCatalogSessionStorageKey(baseKey, session);
  if (!key || !prepareCatalogSessionStorage(session)) return false;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
    return isCatalogStorageSessionCurrent(session);
  } catch {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // Cache cleanup is best effort.
    }
    return false;
  }
};

export const removeCatalogSessionValue = (baseKey, session = getCatalogStorageSession()) => {
  const key = getCatalogSessionStorageKey(baseKey, session);
  if (!key || !prepareCatalogSessionStorage(session)) return false;
  try {
    window.localStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
};
